import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle } from 'lucide-react';
import { FILL_GRADIENT } from '../../constants/gradients';
import { analyticsService } from '../../services/analyticsService';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
  isLoading?: boolean;
  requireTextConfirmation?: boolean;
  confirmationText?: string;
  confirmationPlaceholder?: string;
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isDestructive = false,
  isLoading = false,
  requireTextConfirmation = false,
  confirmationText = '',
  confirmationPlaceholder = 'Type to confirm'
}) => {
  const [inputValue, setInputValue] = useState('');
  const isConfirmDisabled = requireTextConfirmation ? inputValue !== confirmationText : false;

  // Reset input when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      // Track modal opened
      analyticsService.track({
        name: 'modal_opened',
        properties: {
          modal_type: 'confirmation',
          is_destructive: isDestructive,
          requires_text_confirmation: requireTextConfirmation
        }
      });
    } else {
      setInputValue('');
    }
  }, [isOpen, isDestructive, requireTextConfirmation]);

  // Close modal on escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isLoading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, isLoading, onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            onClick={!isLoading ? onClose : undefined}
            className="fixed inset-0 bg-black bg-opacity-50 z-[100] flex items-center justify-center p-4"
            style={{ willChange: 'opacity' }}
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-dark-surface rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
              style={{ willChange: 'opacity, transform' }}
            >
              {/* Gradient Line at Top (only for destructive) */}
              {isDestructive && (
                <div className="h-1 w-full bg-gradient-to-r from-red-500 to-orange-500" />
              )}

              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-dark-border">
                <div className="flex items-center gap-3">
                  {isDestructive && (
                    <div className="w-10 h-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
                    </div>
                  )}
                  <h3 className="text-xl font-bold text-gray-900 dark:text-dark-text">
                    {title}
                  </h3>
                </div>
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-600 dark:text-dark-text-muted leading-relaxed">
                  {message}
                </p>
                
                {requireTextConfirmation && (
                  <div className="mt-4 space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Type <span className="font-mono font-semibold text-red-600 dark:text-red-400">{confirmationText}</span> to confirm:
                    </label>
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder={confirmationPlaceholder}
                      className="w-full px-4 py-2.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg border border-gray-300 dark:border-gray-600 focus:border-red-500 dark:focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/20 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
                      autoFocus
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-dark-border bg-gray-50 dark:bg-gray-800/50">
                <button
                  onClick={onClose}
                  disabled={isLoading}
                  className="px-5 py-2.5 text-sm font-semibold text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
                <button
                  onClick={onConfirm}
                  disabled={isLoading || isConfirmDisabled}
                  className={`px-6 py-2.5 text-sm font-semibold text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    isDestructive
                      ? 'bg-red-600 hover:bg-red-700 hover:shadow-lg hover:scale-105 active:scale-95'
                      : 'hover:shadow-lg hover:scale-105 active:scale-95'
                  }`}
                  style={!isDestructive ? { background: FILL_GRADIENT } : undefined}
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Processing...
                    </span>
                  ) : (
                    confirmText
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ConfirmationModal; 